/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/partners/Customers.tsx
 * ----------------------------------------------------------------------------
 * Lista de Clientes com deteção automática silenciosa do endpoint.
 *
 * Ordem de tentativa:
 *   - /api/partners/customers/
 *   - /api/partners/customer/
 *   - /api/partners/clients/
 *   - /api/partners/clientes/
 *
 * Dicas:
 *  - Pode forçar via querystring:  /partners/customers?ep=customers
 *    Valores: customers | customer | clients | clientes
 *  - Se nenhum endpoint responder com 2xx, usamos o primeiro da lista
 *    apenas para não quebrar a tela e registamos um aviso no console.
 * ========================================================================== */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import CrudList from '@/components/CrudList'
import { apiGet } from '@/lib/http'

type EpKey = 'customers' | 'customer' | 'clients' | 'clientes'

const EP_MAP: Record<EpKey, string> = {
  customers: '/api/partners/customers/',
  customer: '/api/partners/customer/',
  clients: '/api/partners/clients/',
  clientes: '/api/partners/clientes/',
}

const CANDIDATES: string[] = [
  EP_MAP.customers,
  EP_MAP.customer,
  EP_MAP.clients,
  EP_MAP.clientes,
]

export default function Customers() {
  const [endpoint, setEndpoint] = useState<string | null>(null)

  // permite forçar via ?ep=customers|customer|clients|clientes
  const forced = useMemo(() => {
    const qs = new URLSearchParams(location.search)
    const raw = (qs.get('ep') || '').toLowerCase() as EpKey | ''
    return (raw && EP_MAP[raw as EpKey]) || null
  }, [])

  useEffect(() => {
    ;(async () => {
      // 1) override manual (querystring)
      if (forced) {
        setEndpoint(forced)
        return
      }

      // 2) Varre candidatos até achar um endpoint que responda 2xx
      for (const ep of CANDIDATES) {
        try {
          await apiGet(`${ep}?page=1&page_size=1`)
          setEndpoint(ep)
          return
        } catch {
          // tenta o próximo
        }
      }

      // 3) Nenhum funcionou → mantém o primeiro para não quebrar a tela
      //    e apenas regista um aviso no console (sem mostrar faixa visual).
      console.warn(
        '[Customers] Nenhum endpoint válido encontrado para Clientes; ' +
          'a usar fallback:',
        CANDIDATES[0],
        ' (tentativas: customers, customer, clients, clientes)',
      )
      setEndpoint(CANDIDATES[0])
    })()
  }, [forced])

  // Evita render até decidir o endpoint
  if (!endpoint) {
    return (
      <div className="p-4 md:p-6">
        <div className="text-sm opacity-70">A carregar lista de clientes…</div>
      </div>
    )
  }

  return (
    <CrudList
      title="Clientes"
      endpoint={endpoint}
      writable
      canDelete={false}
      rowActions={(row: { id: number }) => (
        // ADR-0056 C1c-2: contratos de shelf-life deste cliente
        <Link
          className="btn subtle"
          title="Contratos de shelf-life deste cliente"
          to={`/partners/customers/${row.id}/shelf-life-contracts`}
        >
          Contratos
        </Link>
      )}
      columns={[
        { key: 'code', header: 'Código', className: 'whitespace-nowrap' },
        { key: 'name', header: 'Nome', className: 'min-w-[220px]' },
        { key: 'city', header: 'Cidade' },
        { key: 'packaging_policy', header: 'Embalagem' },
        { key: 'is_active', header: 'Ativo' },
      ]}
    />
  )
}
